import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getDashboardData } from '../API/queryApi';
import { RootState } from '../store/store';

interface DashboardState {
    data: any | null;
    loading: boolean;
    error: string | null;
    reportList: any[];
    queryList: any[];
    documentList: any[];
    multipleTargetsList: any[];
    noTargetList: any[];
    usageStats: UsageStats;
    activeSearches: ActiveSearches;
}

interface UsageStats {
    totalSearchCount: number;
    totalReportCount: number;
    totalDocumentUploadCount: number;
}

interface ActiveSearches {
    total: number;
    completed: number;
    percent: number;
    period: string;
    processing: number;
}

const initialState: DashboardState = {
    data: null,
    // Initial value is `true` so the dashboard renders its scoped loading affordances on first
    // paint (before `fetchDashboardData.pending` fires). Do NOT consume `selectDashboardLoading`
    // outside the Dashboard route without re-checking this assumption.
    loading: true,
    error: null,
    reportList: [],
    queryList: [],
    documentList: [],
    multipleTargetsList: [],
    noTargetList: [],
    usageStats: {
        totalSearchCount: 0,
        totalReportCount: 0,
        totalDocumentUploadCount: 0,
    },
    activeSearches: {
        total: 0,
        completed: 0,
        percent: 0,
        period: '',
        processing: 0,
    },
};

export const fetchDashboardData = createAsyncThunk(
    'dashboard/fetchData',
    async () => {
        const response = await getDashboardData();
        return response.data;
    }
);

export const dashboardSlice = createSlice({
    name: 'dashboard',
    initialState,
    reducers: {
        setReportList: (state, action) => {
            state.reportList = action.payload;
        },
        setQueryList: (state, action) => {
            state.queryList = action.payload;
        },
        setDocumentList: (state, action) => {
            state.documentList = action.payload;
        },
        setMultipleTargetsQueryList: (state, action) => {
            state.multipleTargetsList = action.payload;
        },
        setNoTargetList: (state, action) => {
            state.noTargetList = action.payload;
        },
        resetDashboardData: (state) => {
            state.data = null;
            state.loading = true;
            state.error = null;
            state.reportList = [];
            state.queryList = [];
            state.documentList = [];
            state.multipleTargetsList = [];
            state.noTargetList = [];
            state.usageStats = {
                totalSearchCount: 0,
                totalReportCount: 0,
                totalDocumentUploadCount: 0,
            };
            state.activeSearches = {
                total: 0,
                completed: 0,
                percent: 0,
                period: '',
                processing: 0,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDashboardData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
                state.reportList = action.payload.reports || [];
                state.queryList = action.payload.searches || [];
                state.documentList = action.payload.documents || [];
                state.multipleTargetsList = action.payload.multiplyTargetSearches || [];
                state.noTargetList = action.payload.emptySearches || [];
                state.usageStats = action.payload.usageStats || [];
                state.activeSearches = action.payload.activeSearches || [];
            })
            .addCase(fetchDashboardData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch dashboard data';
            });
    },
});

export const { setReportList, setQueryList, setDocumentList, setMultipleTargetsQueryList, setNoTargetList, resetDashboardData } = dashboardSlice.actions;

export const selectDashboardLoading = (state: RootState) => state.dashboard.loading;
export const selectReportList = (state: RootState) => state.dashboard.reportList;
export const selectQueryList = (state: RootState) => state.dashboard.queryList;
export const selectDocumentList = (state: RootState) => state.dashboard.documentList;
export const selectMultipleTargetsList = (state: RootState) => state.dashboard.multipleTargetsList;
export const selectNoTargetList = (state: RootState) => state.dashboard.noTargetList;
export const selectUsageStats = (state: RootState) => state.dashboard.usageStats;
export const selectActiveSearches = (state: RootState) => state.dashboard.activeSearches;
export default dashboardSlice.reducer;
